import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ticketShowService from '../services/ticketShowService';

/**
 * GuardianTicketGuard - Wrapper component that checks if a guardian has access to a ticket
 * Redirects to guardian-dashboard if the ticket doesn't belong to the guardian
 */
const GuardianTicketGuard = ({ children }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isGuardian, getGuardianId } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkTicketAccess = async () => {
      // Only check for guardians
      if (!isGuardian() || !id) {
        setIsAuthorized(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Try to fetch the ticket - backend will return 403 if guardian doesn't have access
        const ticket = await ticketShowService.getTicket(id);
        
        if (ticket && ticket.id) {
          const guardianId = getGuardianId();
          
          // Check if guardian is the ticket reporter
          const isTicketReporter = ticket.guardian_id === parseInt(guardianId);
          
          // If guardian is the reporter, they have access
          if (isTicketReporter) {
            setIsAuthorized(true);
            setLoading(false);
            return;
          }
          
          // If ticket has guardian_view enabled and guardian has active rent at the property
          // Backend already handles this check, so if we got the ticket, they have access
          if (ticket.guardian_view === 1 || ticket.guardian_view === true) {
            setIsAuthorized(true);
            setLoading(false);
            return;
          }
          
          // If we reach here, guardian doesn't have access
          console.log('🚫 Guardian does not have access to ticket:', id);
          navigate('/guardian-dashboard', { replace: true });
        } else {
          // Ticket not found or invalid response
          console.log('⚠️ Ticket not found or invalid:', id);
          navigate('/guardian-dashboard', { replace: true });
        }
      } catch (error) {
        // Check if error message contains 403 status
        const errorMessage = error.message || '';
        const is403Error = errorMessage.includes('403') || 
                          errorMessage.includes('permission') ||
                          errorMessage.includes('Access denied') ||
                          errorMessage.includes('do not have permission');
        
        if (is403Error) {
          console.log('🚫 Access denied for ticket:', id);
          navigate('/guardian-dashboard', { replace: true });
          return;
        }
        
        // For other errors (like 404), still redirect to be safe
        console.error('❌ Error checking ticket access:', error);
        navigate('/guardian-dashboard', { replace: true });
      }
    };

    checkTicketAccess();
  }, [id, isGuardian, getGuardianId, navigate]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isAuthorized) {
    return null; // Redirect is happening
  }

  return <>{children}</>;
};

export default GuardianTicketGuard;

